/**
 * Tasks React Query Hooks
 *
 * Custom hooks for task CRUD operations using React Query.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  tasksApi,
  type TaskFilters,
  type CreateTaskInput,
  type UpdateTaskInput,
} from '../../lib/tasksApi';

// Query keys for cache management
export const taskKeys = {
  all: ['tasks'] as const,
  lists: () => [...taskKeys.all, 'list'] as const,
  list: (filters?: TaskFilters) => [...taskKeys.lists(), filters] as const,
  details: () => [...taskKeys.all, 'detail'] as const,
  detail: (id: number) => [...taskKeys.details(), id] as const,
};

/**
 * Hook to fetch task list with filters
 */
export function useTasks(filters?: TaskFilters) {
  return useQuery({
    queryKey: taskKeys.list(filters),
    queryFn: () => tasksApi.list(filters),
  });
}

/**
 * Hook to fetch single task by ID
 */
export function useTask(id: number | undefined) {
  return useQuery({
    queryKey: taskKeys.detail(id!),
    queryFn: () => tasksApi.get(id!),
    enabled: !!id,
  });
}

/**
 * Hook to create task
 */
export function useCreateTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateTaskInput) => tasksApi.create(input),
    onSuccess: () => {
      // Invalidate task list cache
      queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
    },
  });
}

/**
 * Hook to update task
 */
export function useUpdateTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: UpdateTaskInput }) =>
      tasksApi.update(id, input),
    onSuccess: (data, { id }) => {
      // Update cache with new data
      queryClient.setQueryData(taskKeys.detail(id), data);
      // Invalidate list cache
      queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
    },
  });
}

/**
 * Hook to delete task
 */
export function useDeleteTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => tasksApi.delete(id),
    onSuccess: (_, id) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: taskKeys.detail(id) });
      // Invalidate list cache
      queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
    },
  });
}
